import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  selectProposalCollisions,
  selectProposalEnvelope,
  selectProposalPendingChanges,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { proposalFieldHasCollision } from "work/proposals/negotiation/proposal-field-collision";
import { proposalFieldHasPendingChange } from "work/proposals/negotiation/proposal-field-pending-change";
import { proposalFieldUnread } from "work/proposals/negotiation/proposal-field-unread";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

// Anchors the status badge to the top-right corner of whatever the badge sits on (an icon, an
// input's label, ...). The consumer is responsible for a `position: relative` (or similar) ancestor.
export const ProposalFieldChangeBadgeAnchor = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  position: "absolute",
  right: -8,
  top: -8,
  zIndex: 1,
}));

// One capsule carries both pieces of state. Keeping the count and collision marker in one visual unit
// avoids making the history icon look as though it was badged twice, while the divider keeps the two
// meanings legible when they coexist.
const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{
  tone: "unread" | "collision";
}>(({ theme, tone }) => ({
  alignItems: "center",
  backgroundColor: tone === "collision" ? theme.palette.error.main : theme.palette.secondary.main,
  borderRadius: 999,
  boxSizing: "border-box",
  color: tone === "collision" ? theme.palette.error.contrastText : theme.palette.secondary.contrastText,
  display: "inline-flex",
  gap: 3,
  fontSize: "0.55rem",
  fontWeight: 700,
  height: 18,
  justifyContent: "center",
  lineHeight: 1,
  minWidth: 18,
  padding: "0 4px",
  position: "relative",
  zIndex: 1,
}));

const StatusDivider = styled(Box)(({ theme }) => ({
  alignSelf: "center",
  backgroundColor: theme.palette.error.contrastText,
  height: 10,
  opacity: 0.55,
  width: 1,
}));

export type ProposalFieldChangeBadgeState = {
  /** Unviewed content changes on the field, not authored by the viewer (see proposal-field-unread). */
  unreadCount: number;
  /** Highest stream sequence among the unread entries — the value to mark seen through. */
  latestUnseenSequence: number;
  /** Whether the viewer's own side has an unresolved collision on the field (see proposal-field-collision). */
  isCollision: boolean;
  /** Whether this field has a local content change queued for the next Save. */
  hasPendingChange: boolean;
  /** Whether either signal above should render a bubble at all. */
  showBadge: boolean;
  /** Accessible label summarizing whichever signal(s) are showing; undefined when nothing is showing. */
  badgeLabel: string | undefined;
};

export function proposalFieldChangeBadgeLabel(unreadCount: number, isCollision: boolean): string | undefined {
  const unreadLabel =
    unreadCount > 0
      ? `${unreadCount} unviewed change${unreadCount === 1 ? "" : "s"} on this field`
      : undefined;

  if (!isCollision) return unreadLabel;
  return unreadLabel
    ? `a change needs your attention; ${unreadLabel}`
    : "a change needs your attention";
}

/**
 * Computes a field's change-badge state from the negotiation Redux state: the unread change count from
 * {@link proposalFieldUnread} and the collision flag from {@link proposalFieldHasCollision}. Shared by
 * {@link ProposalFieldHistoryButton} and any other per-field surface (e.g. a field input's label) that
 * wants the same badge without duplicating the selector wiring.
 */
export function useProposalFieldChangeBadgeState(
  negotiationId: string,
  field: ProposalField | null | undefined,
  currentUserId: string | undefined,
): ProposalFieldChangeBadgeState {
  const envelope = useSelector(selectProposalEnvelope(negotiationId));
  const collisions = useSelector(selectProposalCollisions(negotiationId));
  const pendingChanges = useSelector(selectProposalPendingChanges(negotiationId));

  const { unreadCount, latestUnseenSequence } = proposalFieldUnread(envelope, field, currentUserId);
  const isCollision = proposalFieldHasCollision(collisions, field);
  const hasPendingChange = proposalFieldHasPendingChange(pendingChanges.operations, field);
  const showCount = unreadCount > 0;
  const showBadge = isCollision || showCount;
  // One wording for the collision signal, wherever it renders. The history popover button and the
  // draft page's history dropdown previously named the same state two different ways ("Conflicting
  // change on this field" vs "a change needs your attention"), so the two surfaces of one signal read
  // as two different things — the S1 shape. This phrasing wins because it is the one the field's own
  // tooltip already used and the one the E2E asserts a user can see.
  const badgeLabel = proposalFieldChangeBadgeLabel(unreadCount, isCollision);

  return { unreadCount, latestUnseenSequence, isCollision, hasPendingChange, showBadge, badgeLabel };
}

/** Renders one status capsule for a badge state already computed by {@link useProposalFieldChangeBadgeState}. */
export function ProposalFieldChangeBadgeBubbles({ state }: Readonly<{ state: ProposalFieldChangeBadgeState }>) {
  if (!state.showBadge) return null;

  return (
    <ProposalFieldChangeBadgeAnchor>
      <StatusBadge
        tone={state.isCollision ? "collision" : "unread"}
        aria-label={state.badgeLabel}
        sx={{ right: -4 }}
      >
        {state.isCollision && (
          <Box component="span" aria-hidden="true">
            !
          </Box>
        )}
        {state.isCollision && state.unreadCount > 0 && <StatusDivider aria-hidden="true" />}
        {state.unreadCount > 0 && (
          <Box component="span" aria-hidden="true">
            {state.unreadCount}
          </Box>
        )}
      </StatusBadge>
    </ProposalFieldChangeBadgeAnchor>
  );
}

type ProposalFieldChangeBadgeProps = {
  field: ProposalField;
  negotiationId?: string;
  /** Defaults to the signed-in user; the badge never counts the viewer's own changes as unread. */
  currentUserId?: string;
};

/**
 * A small, self-contained unviewed-change badge for a field input's label or wrapper — the same signal
 * {@link ProposalFieldHistoryButton} shows next to its history icon, without the icon button or popover.
 * Place it inside a `position: relative` container next to the field's label/control. Renders nothing
 * until the negotiation stream exists (drafts before their first save, or the legacy path), matching
 * ProposalFieldHistoryButton so call sites that render this without a negotiation keep working untouched.
 */
export default function ProposalFieldChangeBadge({
  field,
  negotiationId,
  currentUserId,
}: Readonly<ProposalFieldChangeBadgeProps>) {
  if (!negotiationId) return null;

  return (
    <ConnectedProposalFieldChangeBadge field={field} negotiationId={negotiationId} currentUserId={currentUserId} />
  );
}

function ConnectedProposalFieldChangeBadge({
  field,
  negotiationId,
  currentUserId: explicitCurrentUserId,
}: Readonly<{ field: ProposalField; negotiationId: string; currentUserId?: string }>) {
  const session = useSession();
  const currentUserId = explicitCurrentUserId ?? session.user?.id?.value;
  const state = useProposalFieldChangeBadgeState(negotiationId, field, currentUserId);

  return <ProposalFieldChangeBadgeBubbles state={state} />;
}
